export type DateInput = string | Date;

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * Calendar date (`YYYY-MM-DD`) of a content date, taken as written.
 *
 * Content dates are ISO strings, either date-only or full timestamps. We only
 * ever display the day, so the value is truncated rather than converted between
 * timezones: the server, the build machine, and every visitor's browser all
 * agree on the same day. `Date` objects (YAML frontmatter parsed by gray-matter)
 * are UTC midnight of the written date, so their ISO form truncates the same way.
 */
export function toCalendarDate(value: DateInput): string {
  const iso = value instanceof Date ? value.toISOString() : value;
  const match = /^\d{4}-\d{2}-\d{2}/.exec(iso);
  if (!match) throw new Error(`Expected an ISO date, got "${String(value)}"`);
  return match[0];
}

/** Calendar month (`YYYY-MM`) of a content date, taken as written like {@link toCalendarDate}. */
export function toCalendarMonth(value: DateInput): string {
  return toCalendarDate(value).slice(0, 7);
}

/** Month label for a `YYYY-MM` calendar month: "February 2026". */
export function formatMonth(calendarMonth: string): string {
  const [year, month] = calendarMonth.split("-").map(Number);
  return `${MONTHS[month - 1]} ${year}`;
}

/**
 * The site-wide date label: "February 24, 2026", or "Feb 24, 2026" when
 * `compact` is set for tight spots (cards, tags, navigation).
 */
export function formatDate(
  value: DateInput,
  { compact = false }: { compact?: boolean } = {},
): string {
  const [year, month, day] = toCalendarDate(value).split("-").map(Number);
  const monthName = MONTHS[month - 1];
  return `${compact ? monthName.slice(0, 3) : monthName} ${day}, ${year}`;
}
